<script setup>
import { computed, ref } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { formatTime } from '@/utils/datetime'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import ReportTextSelectionPopover from './ReportTextSelectionPopover.vue'
import ReportSettingsDialog from './ReportSettingsDialog.vue'

// Configure marked options
marked.setOptions({
  breaks: true,
  gfm: true,
  smartLists: true,
  smartypants: true
})

const store = useStore()
const route = useRoute()
const router = useRouter()
const settings = computed(() => store.getters['user/getUserSettings'] || { tz: 'UTC' })

const props = defineProps({
  report: {
    type: Object,
    required: true
  },
  sessionUuid: {
    type: String,
    default: null
  },
  organizationId: {
    type: Number,
    default: null
  },
  enableTextSelection: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['version-created', 'report-updated'])

const contentRef = ref(null)

const currentMember = computed(() => store.getters['user/getMember'])
const isOwner = computed(() => {
  return currentMember.value?.member_id === props.report?.member_id
})

const handleReportUpdated = (updatedReport) => {
  emit('report-updated', updatedReport)
}

const canShowTextSelection = computed(() => {
  return props.enableTextSelection && props.sessionUuid && props.organizationId && props.report?.report_uuid
})

const handleVersionCreated = () => {
  // Just bubble up the event - the new version will be created via WebSocket
  // The parent component should refresh the report after LLM response completes
  emit('version-created')
}

const renderedContent = computed(() => {
  if (!props.report?.content) return ''
  const htmlContent = marked.parse(props.report.content)
  return DOMPurify.sanitize(htmlContent)
})

const formattedDate = computed(() => {
  return formatTime(props.report.created_at, settings.value.tz, 'MMMM D, YYYY [at] HH:mm')
})

// Version selector
const availableVersions = computed(() => {
  return props.report?.available_versions || []
})

const hasMultipleVersions = computed(() => {
  return availableVersions.value.length > 1
})

const currentVersion = computed(() => {
  // Get version from route query or use the report's current version
  const queryVersion = route.query.version
  if (queryVersion) {
    return parseInt(queryVersion, 10)
  }
  return props.report?.version
})

const selectedVersion = computed({
  get: () => currentVersion.value?.toString() || '',
  set: (value) => {
    const version = parseInt(value, 10)
    const latestVersion = availableVersions.value[0]

    // Update route with version query param
    if (version === latestVersion) {
      // Remove version param for latest version
      const query = { ...route.query }
      delete query.version
      router.push({ query })
    } else {
      router.push({ query: { ...route.query, version } })
    }
  }
})
</script>

<template>
  <div class="h-full flex flex-col overflow-hidden bg-background">
    <!-- Header -->
    <div class="px-4 py-3 border-b border-border flex-shrink-0">
      <div class="flex items-start justify-between">
        <div class="flex-1 min-w-0">
          <h1 class="text-lg font-semibold truncate">{{ report.title || 'Untitled Report' }}</h1>
          <div class="flex items-center gap-1.5 mt-1">
            <span class="text-xs text-muted-foreground">{{ formattedDate }}</span>
            <Badge v-if="report.is_public" variant="default" class="text-xs px-1.5 py-0 h-4">
              Public
            </Badge>
            <ReportSettingsDialog
              v-if="isOwner && organizationId"
              :report="report"
              :organization-id="organizationId"
              @report-updated="handleReportUpdated"
            />
          </div>
        </div>

        <!-- Version selector -->
        <div v-if="hasMultipleVersions" class="ml-3 flex-shrink-0">
          <Select v-model="selectedVersion">
            <SelectTrigger class="w-[120px]">
              <SelectValue>
                Version {{ currentVersion }}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem
                  v-for="v in availableVersions"
                  :key="v"
                  :value="v.toString()"
                >
                  <span>
                    Version {{ v }}
                    <span v-if="v === availableVersions[0]" class="text-muted-foreground ml-1">(latest)</span>
                  </span>
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div ref="contentRef" class="flex-1 overflow-y-auto p-4 relative">
      <div v-if="report.content" class="prose prose-sm dark:prose-invert max-w-none break-words">
        <div v-html="renderedContent" class="break-words"></div>
      </div>
      <div v-else class="text-center text-muted-foreground py-8">
        <p>This report has no content.</p>
      </div>

      <!-- Text Selection Popover -->
      <ReportTextSelectionPopover
        v-if="canShowTextSelection && contentRef"
        :report-uuid="report.report_uuid"
        :report-version="report.version"
        :session-uuid="sessionUuid"
        :organization-id="organizationId"
        :content-ref="contentRef"
        @version-created="handleVersionCreated"
      />
    </div>
  </div>
</template>
